// Code 128 barcode width calculation utilities

export interface BarcodeCalculation {
  widthInDots: number;
  heightInDots: number;
  characterCount: number;
  codeSet: "A" | "B" | "C" | "Mixed";
  textHeight: number; // Height needed for text below barcode
}

/**
 * Calculate the accurate dimensions of a Code 128 barcode based on its content
 * Formula: W = [11(C + 2) + 2]X where C = number of symbol characters, X = narrow bar width
 */
export function calculateCode128Width(
  content: string,
  narrowBarWidth = 1, // X dimension in dots
  _dpi = 203,
  barcodeHeight = 50, // Height of barcode bars in dots
  showText = true, // Whether text is shown below barcode
  fontSize = 12, // Font size for text below barcode
): BarcodeCalculation {
  if (!content) {
    return {
      widthInDots: 0,
      heightInDots: barcodeHeight,
      characterCount: 0,
      codeSet: "B",
      textHeight: 0,
    };
  }

  // Determine optimal character set and count symbol characters
  const { characterCount, codeSet } = getOptimalCode128Encoding(content);

  // Code 128 formula: [11(C + 2) + 2]X
  // C = number of data characters (including check digit)
  // +2 accounts for start and stop characters
  // +2 at the end accounts for the extra 2 modules in stop character (13 vs 11)
  const totalModules = 11 * (characterCount + 2) + 2;
  const widthInDots = totalModules * narrowBarWidth;

  // Calculate text height if text is shown below barcode
  // Typical text height is about 1.2x font size to account for spacing
  const textHeight = showText ? Math.ceil(fontSize * 1.2) : 0;
  const totalHeight = barcodeHeight + textHeight;

  return {
    widthInDots,
    heightInDots: totalHeight,
    characterCount,
    codeSet,
    textHeight,
  };
}

/**
 * Determine the optimal Code 128 character set and count characters
 */
function getOptimalCode128Encoding(content: string): {
  characterCount: number;
  codeSet: "A" | "B" | "C" | "Mixed";
} {
  // Check if content is all numeric (Code Set C is most efficient)
  if (/^\d+$/.test(content)) {
    // Code Set C encodes 2 digits per character
    const characterCount = Math.ceil(content.length / 2) + 1; // +1 for check digit
    return { characterCount, codeSet: "C" };
  }

  // Check if content has lowercase letters (requires Code Set B)
  if (/[a-z]/.test(content)) {
    const characterCount = content.length + 1; // +1 for check digit
    return { characterCount, codeSet: "B" };
  }

  // Check if content has control characters (requires Code Set A)
  // Control characters are ASCII 0-31
  if (content.split("").some((char) => char.charCodeAt(0) < 32)) {
    const characterCount = content.length + 1; // +1 for check digit
    return { characterCount, codeSet: "A" };
  }

  // Default to Code Set B for mixed alphanumeric
  const characterCount = content.length + 1; // +1 for check digit
  return { characterCount, codeSet: "B" };
}

/**
 * Calculate barcode dimensions in dots based on DPI and physical requirements
 */
export function calculateBarcodeWidthForDPI(
  content: string,
  dpi: number,
  minXDimension = 0.0075, // 7.5 mils minimum per Code 128 spec
  barcodeHeight = 50,
  showText = true,
  fontSize = 12,
): BarcodeCalculation {
  // Convert minimum X dimension from inches to dots
  const minXDots = Math.ceil(minXDimension * dpi);
  const narrowBarWidth = Math.max(1, minXDots);

  return calculateCode128Width(
    content,
    narrowBarWidth,
    dpi,
    barcodeHeight,
    showText,
    fontSize,
  );
}
